'use client';

import { getAppModules } from 'korvo-app-module-registry';
import Link from 'next/link';
import { useMemo } from 'react';

import { ModuleIcon } from '../shared/Icon/ModuleIcon';
import { Logo } from '../shared/Logo';

const PUNS: string[] = [
  'Caws and effect: input in, value out.',
  'Nevermore latency—responses that fly.',
  'Clever as a crow, scalable as the cloud.',
  "We don't wing it—we measure and iterate.",
  'Murder the noise, keep the signal.',
];

export type HomepageExtraCard = {
  href: string;
  label: string;
  icon: string;
};

/** Same shape as HomepageExtraCard; optional id for data-testid (e.g. homepage-app-{id}) */
export type HomepageRegistryCard = HomepageExtraCard & { id?: string };

export type NormalHomepageProps = {
  /** Profile/settings URL for in-page link (default: /profile) */
  profileHref?: string;
  /** Extra cards to show in the app grid (e.g. Admin for admin users) */
  extraCards?: HomepageExtraCard[];
  /** Installed/marketplace app cards to show in the same grid (icon may be URL or Remix icon class) */
  registryCards?: HomepageRegistryCard[];
};

const CARD_CLASS =
  'normal-homepage-card flex flex-col items-center justify-center p-6 md:p-6 rounded-lg bg-white dark:bg-background-secondary shadow-subtle group border border-foreground/10 w-32 sm:w-36 md:w-40 h-28';

export function NormalHomepage({ profileHref = '/profile', extraCards = [], registryCards = [] }: NormalHomepageProps = {}) {
  const availableModules = getAppModules();
  const pun = useMemo(() => {
    const idx = (availableModules?.length ?? 0) % PUNS.length;
    return PUNS[idx];
  }, [availableModules?.length]);

  const hasCards = availableModules?.length > 0 || extraCards.length > 0 || registryCards.length > 0;

  // No padding-top for topbar: when used inside ShellLayout, the topbar is in-flow so main content
  // already starts below it; extra pt would reserve space even when topbar is hidden (e.g. on mobile).
  return (
    <div className="max-w-7xl mx-auto p-6 md:p-8 korvo-main-min-height">
      <div className="w-full">
        <div className="text-center flex flex-col items-center mt-6 md:mt-10">
          <div className="flex flex-col items-center gap-2">
            <Logo size="lg" />
            <p className="text-xl md:text-2xl text-foreground">Welcome to Project-Korvo</p>
          </div>
          <p className="mt-3 md:mt-4 text-sm md:text-base text-foreground-muted">{pun}</p>
          {hasCards && (
            <div className="mt-10 md:mt-12 w-full flex flex-wrap justify-center items-center gap-6 md:gap-8 px-4">
              {availableModules?.map((module) => {
                const iconClass: Record<string, string> = {
                  chat: 'ri-chat-1-line',
                  editor: 'ri-file-text-line',
                  mentor: 'ri-target-line',
                };
                const label: Record<string, string> = {
                  chat: 'Multi-Chat',
                  editor: 'Document Editor',
                  mentor: 'Mentor',
                };
                return (
                  <a
                    key={module}
                    href={`/${module}`}
                    className={CARD_CLASS}
                  >
                    <ModuleIcon
                      icon={iconClass[module] || 'ri-apps-2-line'}
                      className="h-6 w-6 mb-2 text-foreground"
                    />
                    <span className="text-sm text-foreground group-hover:text-foreground text-center">{label[module] || module}</span>
                  </a>
                );
              })}
              {extraCards.map((card) => (
                <Link
                  key={card.href}
                  href={card.href}
                  className={CARD_CLASS}
                  data-testid={`homepage-card-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <ModuleIcon icon={card.icon} className="h-6 w-6 mb-2 text-foreground" />
                  <span className="text-sm text-foreground group-hover:text-foreground text-center">{card.label}</span>
                </Link>
              ))}
              {registryCards.map((card) => (
                <Link
                  key={card.href}
                  href={card.href}
                  className={CARD_CLASS}
                  data-testid={card.id ? `homepage-app-${card.id}` : undefined}
                >
                  {card.icon.startsWith('/') ? (
                    <img src={card.icon} alt="" className="h-6 w-6 mb-2 object-contain" />
                  ) : (
                    <ModuleIcon icon={card.icon} className="h-6 w-6 mb-2 text-foreground" />
                  )}
                  <span className="text-sm text-foreground group-hover:text-foreground text-center line-clamp-2">{card.label}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

